import type {
  NuggetCitationSource,
  NuggetDocumentContext,
  NuggetDocumentSource,
  NuggetSource,
} from "@/features/nuggets/model/nuggetsSlice";

/** Moved to `@/shared/lib/citations` when chat gained the same numbered-source treatment.
 * Re-exported here so every nugget call site keeps its import path. */
export {
  documentSourceMetaLine,
  hasCitationMarkers,
  isDocumentSource,
  isSafeSourceUrl,
  parseCitedText,
  sortSourcesById,
  sourceHostname,
  sourceLabel,
  sourcesById,
  stripCitationMarkers,
  type CitedTextSegment,
} from "@/shared/lib/citations";

/** Every source the card's `[n]` markers can resolve against: its web sources plus a row per
 * cited document page.
 *
 * Document ids are taken from `document_context` **as authored** rather than renumbered here.
 * The pipeline wrote those numbers into the card's prose (see `document_statement_text`), so a
 * reader that renumbered them would point `[2]` at a different page than the sentence carrying
 * it. On the vanishingly unlikely id collision — a card holding both manual web sources and
 * document context — the web source wins, matching `sourcesById`'s own first-one-wins rule. */
export function collectNuggetSources(
  sources: NuggetSource[] | undefined,
  documentContext: NuggetDocumentContext | undefined,
): NuggetCitationSource[] {
  const webSources = sources ?? [];
  const taken = new Set(webSources.map((source) => source.id));
  const documentSources: NuggetDocumentSource[] = (documentContext?.source_refs ?? [])
    .filter((ref) => !taken.has(ref.id))
    .map((ref) => ({
      kind: "document",
      id: ref.id,
      source_file: ref.source_file,
      page: ref.page,
      doc_title: ref.doc_title,
      timeline: ref.timeline,
      is_image: ref.is_image,
      preview_image: ref.preview_image,
    }));
  return [...webSources, ...documentSources];
}
